import type { MultimodalAssetDto } from '@scale/mm-shared';
import { ImageCategory } from '@scale/mm-shared';
import { Tag, Types } from '@scale/scaleui';

const ImageCategoryColors = {
  [ImageCategory.NATURAL]: 'info',
  [ImageCategory.QUANTITATIVE]: 'neutral',
  [ImageCategory.MMMU]: 'warning',
  [ImageCategory.UNVALIDATED]: 'warning',
  [ImageCategory.ART]: 'info',
  [ImageCategory.BUSINESS]: 'info',
  [ImageCategory.HEALTH]: 'info',
  [ImageCategory.HUMANITIES]: 'info',
  [ImageCategory.SCIENCE]: 'info',
  [ImageCategory.TECH]: 'info',
} as const;

interface ImageMetadataTagsProps {
  image: Partial<MultimodalAssetDto> & { image_url: string };
}

/**
 * Stringifies a byte count using its largest unit e.g. 1000000 -> "1 MB"
 * Adapted from https://stackoverflow.com/a/18650828
 */
function formatBytes(bytes: number, decimals: number = 0, uppercase: boolean = true): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const sizeSuffix = uppercase ? sizes[i] : sizes[i].toLowerCase();

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizeSuffix;
}

const ImageMetadataTags = ({ image }: ImageMetadataTagsProps) => {
  let image_category_variant: Types.Variant = 'primary';
  if (
    !!image.image_category &&
    image.image_category.length === 1 &&
    image.image_category[0] in ImageCategoryColors
  ) {
    image_category_variant = ImageCategoryColors[image.image_category[0]];
  }

  return (
    <div className="mt-2 max-w-[80vw] flex gap-2 flex-wrap">
      {!!image.image_extension && (
        <Tag
          onClick={function noRefCheck() {}}
          size="sm"
          variant="primary"
          className="mb-1"
          key="extension"
        >
          extension: {image.image_extension}
        </Tag>
      )}
      {!!image.image_licenses && image.image_licenses.length > 0 && (
        <Tag
          onClick={function noRefCheck() {}}
          size="sm"
          variant="primary"
          className="mb-1"
          key="licenses"
        >
          licenses: {image.image_licenses.join(', ')}
        </Tag>
      )}
      {!!image.image_size && (
        <Tag
          onClick={function noRefCheck() {}}
          size="sm"
          variant="primary"
          className="mb-1"
          key="image_size"
        >
          size: {formatBytes(image.image_size)}
        </Tag>
      )}
      {!!image.image_dimensions && (
        <Tag
          onClick={function noRefCheck() {}}
          size="sm"
          variant="primary"
          className="mb-1"
          key="dimensions"
        >
          dimensions: {`${image.image_dimensions.width}x${image.image_dimensions.height}`}
        </Tag>
      )}

      {/* TODO: hiding image category for now since the values are inaccurate */}
      {!!image.image_category && image.image_category.length > 0 && false && (
        <Tag
          onClick={function noRefCheck() {}}
          size="sm"
          variant={image_category_variant}
          className="mb-1"
          key="category"
        >
          category: {image.image_category!.join(', ')}
        </Tag>
      )}
    </div>
  );
};

export default ImageMetadataTags;
